/**
 * WarRoomBoard.jsx · v3.9.4 · Tablero 2x2 del War Room
 * ═══════════════════════════════════════════════════════════════════
 * Reemplaza la vista full-screen previa por un tablero de 4 cuadrantes
 * fijos, útil para pantallas grandes de dispatch (tipo cabina de control):
 *
 *   ┌───────────────────┬──────────────────┐
 *   │ C1 · MAPA         │ C2 · CALENDARIO  │
 *   │  Pins técnicos    │  OTs de hoy      │
 *   │  Live tracking    │  por técnico     │
 *   ├───────────────────┼──────────────────┤
 *   │ C3 · RUTAS        │ C4 · KPIs        │
 *   │  Polylines + KM   │  + Filtros multi │
 *   └───────────────────┴──────────────────┘
 *
 * Reusa los endpoints del War Room legacy:
 *   GET /v1/warroom/layers, /tech-summary, /v1/mesa/branches/tree.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MapContainer, TileLayer, CircleMarker, Polyline, Marker, Popup,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Layout from "../components/Layout";
import api from "../lib/api";
import {
  Calendar as CalIcon, ChevronLeft, ChevronRight, Clock, Loader2, MapPin,
  Route as RouteIcon, RefreshCcw, Users, Gauge, Milestone, CheckCircle2,
  Filter, Maximize2, Radio,
} from "lucide-react";

// Utilidades
const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const occupationColor = (pct) => {
  if (pct == null) return "#64748B";
  if (pct >= 90) return "#B91C1C";
  if (pct >= 70) return "#D97706";
  if (pct >= 40) return "#059669";
  return "#0EA5E9";
};

const EC_CENTER = [-1.8312, -78.1834]; // Ecuador
const DEFAULT_ZOOM = 7;

export default function WarRoomBoard() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [layers, setLayers] = useState({ techs: [], orders: [], routes: [] });
  const [summary, setSummary] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState(new Set());
  const [selectedZones, setSelectedZones] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchAll = async (opts = {}) => {
    setLoading(true);
    try {
      const params = { date: toISODate(date) };
      if (selectedBranches.size) params.branch_ids = [...selectedBranches].join(",");
      if (selectedZones.size) params.zone_ids = [...selectedZones].join(",");
      const [ly, sm] = await Promise.all([
        api.get("/v1/warroom/layers", { params }),
        api.get("/v1/warroom/tech-summary", { params }),
      ]);
      setLayers(ly.data || { techs: [], orders: [], routes: [] });
      setSummary(sm.data?.items || []);
    } catch (e) {
      if (!opts.silent) toast.error("No se pudo cargar el War Room");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/v1/mesa/branches/tree").then(r => setBranches(r.data.tree || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchAll();
    if (!autoRefresh) return;
    const it = setInterval(() => fetchAll({ silent: true }), 45_000);
    return () => clearInterval(it);
    // eslint-disable-next-line
  }, [date, selectedBranches, selectedZones, autoRefresh]);

  // KPIs consolidados
  const kpis = useMemo(() => {
    const totalTechs = summary.length;
    const totalOTs = summary.reduce((a, t) => a + (t.total_ots || 0), 0);
    const closed = summary.reduce((a, t) => a + (t.closed_ots || 0), 0);
    const pending = totalOTs - closed;
    const totalKm = summary.reduce((a, t) => a + (t.total_km || 0), 0);
    const occs = summary.map(t => t.occupation_pct).filter(x => x != null);
    const avgOcc = occs.length ? occs.reduce((a, b) => a + b, 0) / occs.length : null;
    const overloaded = summary.filter(t => (t.occupation_pct || 0) >= 90).length;
    return { totalTechs, totalOTs, closed, pending, totalKm, avgOcc, overloaded };
  }, [summary]);

  const toggle = (set, setter, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  return (
    <Layout title="War Room · Tablero 2×2"
            subtitle={`Vista táctica · ${toISODate(date)} · ${kpis.totalTechs} técnicos · ${kpis.totalOTs} OTs`}>
      {/* Barra superior */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="warroom-topbar">
        <button onClick={() => setDate(addDays(date, -1))} className="p-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50" data-testid="warroom-prev-day">
          <ChevronLeft size={14} />
        </button>
        <button onClick={() => setDate(new Date())} className="px-3 py-1.5 rounded bg-[#0A3747] text-[#E0E621] text-xs font-bold" data-testid="warroom-today">
          HOY
        </button>
        <button onClick={() => setDate(addDays(date, 1))} className="p-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50" data-testid="warroom-next-day">
          <ChevronRight size={14} />
        </button>
        <div className="text-xs font-bold text-[#0A3747] ml-1">{toISODate(date)}</div>
        <div className="flex-1" />
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
          <Radio size={11} /> Auto-refresh 45s
        </label>
        <button onClick={() => fetchAll()} disabled={loading} className="p-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-60" data-testid="warroom-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
        </button>
        <button onClick={() => navigate("/warroom/full")} className="px-3 py-1.5 rounded bg-white border border-gray-300 text-xs font-bold flex items-center gap-1" title="Vista full-screen legacy">
          <Maximize2 size={12} /> Full-screen
        </button>
      </div>

      {/* Tablero 2×2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 grid-rows-2 gap-3" style={{ height: "calc(100vh - 220px)", minHeight: 700 }}>
        {/* ═════ C1 · MAPA CON PINS ═════ */}
        <QuadPanel title="C1 · Mapa Live" icon={<MapPin size={13} />} testid="warroom-c1-map">
          <MapContainer center={EC_CENTER} zoom={DEFAULT_ZOOM} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
            {(layers.techs || []).map(t => (
              <CircleMarker
                key={t.id}
                center={[t.lat, t.lon]}
                radius={12}
                pathOptions={{
                  color: occupationColor(t.occupation_pct),
                  fillColor: occupationColor(t.occupation_pct),
                  fillOpacity: 0.75,
                  weight: 2,
                }}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold text-[#0A3747]">{t.name}</div>
                    <div>OTs: {t.total_ots} · Cerradas: {t.closed_ots}</div>
                    <div>Ocupación: {t.occupation_pct?.toFixed?.(0) ?? "—"}%</div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
            {(layers.orders || []).filter(o => o.status === "finalizada" || o.status === "pendiente_fiscalizacion").map(o => (
              <CircleMarker
                key={"ot-" + o.id}
                center={[o.lat, o.lon]}
                radius={5}
                pathOptions={{ color: "#059669", fillColor: "#10B981", fillOpacity: 1, weight: 1 }}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold flex items-center gap-1">
                      <CheckCircle2 size={11} color="#059669" /> {o.code}
                    </div>
                    <div>{o.client_name || "—"}</div>
                    <div className="text-slate-500">{o.status}</div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </QuadPanel>

        {/* ═════ C2 · CALENDARIO ADAPTATIVO ═════ */}
        <QuadPanel title="C2 · Calendario · OTs del día" icon={<CalIcon size={13} />} testid="warroom-c2-cal">
          <div className="h-full overflow-y-auto p-2 bg-slate-50">
            {summary.length === 0 && !loading && (
              <div className="text-center text-gray-400 text-xs italic py-10">No hay técnicos activos hoy</div>
            )}
            {summary.map(t => {
              const orders = (layers.orders || []).filter(o => o.tech_id === t.id).sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
              return (
                <details key={t.id} className="bg-white rounded border border-gray-200 mb-2 open:shadow" open={orders.length <= 3}>
                  <summary className="cursor-pointer px-2 py-1.5 flex items-center gap-2 text-xs">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center font-black text-white text-[10px]" style={{ backgroundColor: occupationColor(t.occupation_pct) }}>
                      {(t.name || "?").charAt(0)}
                    </div>
                    <div className="flex-1 truncate font-bold text-[#0A3747]">{t.name}</div>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: occupationColor(t.occupation_pct) + "20", color: occupationColor(t.occupation_pct) }}>
                      {orders.length} OTs · {t.occupation_pct?.toFixed?.(0) ?? "—"}%
                    </span>
                  </summary>
                  <div className="px-2 pb-1.5">
                    {orders.length === 0 && <div className="text-[10px] italic text-gray-400 py-1">Sin OTs</div>}
                    {orders.map(o => (
                      <div key={o.id} className="grid grid-cols-[48px_1fr_auto] gap-1 items-center py-1 border-t border-gray-100 text-[10px]">
                        <span className="font-mono font-bold text-[#0A3747]">{(o.scheduled_at || "").slice(11, 16) || "—"}</span>
                        <span className="truncate">{o.code} · {o.client_name || ""}</span>
                        <StatusChip status={o.status} />
                      </div>
                    ))}
                  </div>
                </details>
              );
            })}
          </div>
        </QuadPanel>

        {/* ═════ C3 · RUTAS + KM ═════ */}
        <QuadPanel title="C3 · Rutas trazadas · KM" icon={<RouteIcon size={13} />} testid="warroom-c3-routes">
          <MapContainer center={EC_CENTER} zoom={DEFAULT_ZOOM} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
            {(layers.routes || []).map(r => {
              if (!r.points || r.points.length < 2) return null;
              const positions = r.points.map(p => [p.lat, p.lon]);
              const color = occupationColor(r.occupation_pct);
              return (
                <div key={r.tech_id}>
                  <Polyline positions={positions} pathOptions={{ color, weight: 3, opacity: 0.75 }} />
                  {/* Flecha final */}
                  <CircleMarker
                    center={positions[positions.length - 1]}
                    radius={6}
                    pathOptions={{ color, fillColor: color, fillOpacity: 1 }}
                  >
                    <Popup><div className="text-xs">
                      <div className="font-bold">{r.tech_name}</div>
                      <div>KM: {r.total_km?.toFixed(2) ?? "—"}</div>
                      <div>Pings: {r.points.length}</div>
                    </div></Popup>
                  </CircleMarker>
                </div>
              );
            })}
          </MapContainer>
          {/* Overlay KM total */}
          <div className="absolute bottom-2 left-2 bg-white/95 rounded border border-gray-200 px-2 py-1 text-[11px] shadow-lg">
            <div className="flex items-center gap-1 font-bold text-[#0A3747]">
              <Milestone size={11} /> KM totales flota: <span className="text-emerald-600">{kpis.totalKm.toFixed(1)}</span>
            </div>
          </div>
        </QuadPanel>

        {/* ═════ C4 · KPIs + FILTROS ═════ */}
        <QuadPanel title="C4 · KPIs & Filtros multi-selección" icon={<Gauge size={13} />} testid="warroom-c4-kpis">
          <div className="h-full overflow-y-auto p-3 space-y-3 bg-white">
            {/* KPIs grandes */}
            <div className="grid grid-cols-2 gap-2">
              <KpiTile label="Ocupación media" value={kpis.avgOcc == null ? "—" : `${kpis.avgOcc.toFixed(0)}%`} color={occupationColor(kpis.avgOcc)} testid="kpi-occ" />
              <KpiTile label="Técnicos" value={kpis.totalTechs} color="#0A3747" testid="kpi-techs" />
              <KpiTile label="OTs cerradas" value={`${kpis.closed} / ${kpis.totalOTs}`} color="#059669" testid="kpi-closed" />
              <KpiTile label="Pendientes" value={kpis.pending} color="#D97706" testid="kpi-pending" />
              <KpiTile label="KM flota" value={kpis.totalKm.toFixed(1)} color="#0891B2" testid="kpi-km" />
              <KpiTile label="Sobrecargados ≥90%" value={kpis.overloaded} color="#B91C1C" testid="kpi-overloaded" />
            </div>

            {/* Filtros */}
            <div className="border border-gray-200 rounded p-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
                <Filter size={10} /> Filtros multi-selección
              </div>

              {branches.length === 0 && <div className="text-[10px] italic text-gray-400">Sin sucursales</div>}
              {branches.map(b => (
                <details key={b.id} className="mb-1.5" open>
                  <summary className="text-[11px] font-bold text-[#0A3747] cursor-pointer flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={selectedBranches.has(b.id)}
                      onChange={() => toggle(selectedBranches, setSelectedBranches, b.id)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`filter-branch-${b.id}`}
                    />
                    {b.name}
                  </summary>
                  <div className="pl-6 mt-1">
                    {(b.zones || []).map(z => (
                      <label key={z.id} className="flex items-center gap-1 text-[10px] py-0.5">
                        <input
                          type="checkbox"
                          checked={selectedZones.has(z.id)}
                          onChange={() => toggle(selectedZones, setSelectedZones, z.id)}
                          data-testid={`filter-zone-${z.id}`}
                        />
                        {z.name}
                      </label>
                    ))}
                  </div>
                </details>
              ))}
              {(selectedBranches.size > 0 || selectedZones.size > 0) && (
                <button
                  onClick={() => { setSelectedBranches(new Set()); setSelectedZones(new Set()); }}
                  className="mt-2 text-[10px] font-bold text-red-600 hover:text-red-800"
                  data-testid="filter-clear"
                >Limpiar filtros</button>
              )}
            </div>
          </div>
        </QuadPanel>
      </div>
    </Layout>
  );
}

// ────────── Sub-componentes ──────────
function QuadPanel({ title, icon, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col relative"
    >
      <header className="px-3 py-1.5 bg-slate-800 text-white flex items-center gap-2 text-[11px] font-bold">
        {icon} {title}
      </header>
      <div className="flex-1 relative overflow-hidden">
        {children}
      </div>
    </section>
  );
}

function KpiTile({ label, value, color, testid }) {
  return (
    <div className="rounded-lg p-2 border" style={{ borderColor: color + "40", backgroundColor: color + "10" }} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-2xl font-black mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const colors = {
    finalizada: ["#065F46", "#10B981"],
    pendiente_fiscalizacion: ["#065F46", "#10B981"],
    en_ejecucion: ["#0369A1", "#0EA5E9"],
    en_ruta: ["#78350F", "#F59E0B"],
    en_sitio: ["#0369A1", "#0891B2"],
    asignada: ["#334155", "#94A3B8"],
  };
  const [tx, bg] = colors[status] || ["#334155", "#CBD5E1"];
  return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ color: tx, backgroundColor: bg + "40" }}>{status}</span>;
}
